import React, { useRef, useState } from 'react';
import { useAuditLogDetail } from '../../services/auditLog.service';
import { ACTION_LABELS, RESOURCE_TYPE_LABELS } from './constants';
import {
  Drawer,
  DrawerResizeHandle,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerSubtitle,
  CloseBtn,
  DrawerBody,
  Field,
  FieldLabel,
  FieldValue,
  StatusChip,
  ActionBadge,
  ErrorBox,
  DrawerSpinner as Spinner,
  ReferenceLink,
  DiffTable,
  DiffRow,
  DiffCell,
  DiffBefore,
  DiffAfter
} from './style';

// Builds a human-readable description from backend-returned fields only
// Renders a before → after table for diff objects like { from: {name: 'a'}, to: {name: 'b'} }
const formatDiffValue = val => {
  if (val === null || val === undefined)
    return <em style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</em>;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  const str = String(val);
  if (!str) return <em style={{ color: '#9ca3af', fontStyle: 'italic' }}>empty</em>;
  return str;
};

// Renders a scrollable mini-table when a diff value contains actual row data
const isRowsData = val => val && typeof val === 'object' && !Array.isArray(val) && 'rows' in val;

const RowsDisplay = ({ data, type }) => {
  const isAfter = type === 'after';
  const color = isAfter ? '#16a34a' : '#dc2626';
  const bg = isAfter ? '#f0fdf4' : '#fef2f2';
  const border = isAfter ? '#bbf7d0' : '#fecaca';
  const badgeStyle = {
    background: bg,
    color,
    border: `1px solid ${border}`,
    borderRadius: 3,
    padding: '1px 5px',
    fontFamily: 'monospace',
    fontSize: 11,
    display: 'inline-block',
    marginBottom: data.rows?.length ? 4 : 0
  };
  const columns = data.rows?.length ? Object.keys(data.rows[0]) : [];
  return (
    <div>
      <span style={badgeStyle}>
        {data.count} row{data.count !== 1 ? 's' : ''}
      </span>
      {columns.length > 0 && (
        <div
          style={{
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: 160,
            border: '1px solid #e5e7eb',
            borderRadius: 4
          }}
        >
          <table
            style={{
              fontSize: 10,
              borderCollapse: 'collapse',
              width: '100%',
              whiteSpace: 'nowrap'
            }}
          >
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {columns.map(col => (
                  <th
                    key={col}
                    style={{
                      padding: '2px 6px',
                      borderBottom: '1px solid #e5e7eb',
                      color: '#374151',
                      fontWeight: 600,
                      position: 'sticky',
                      top: 0,
                      background: '#f9fafb'
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  {columns.map(col => (
                    <td
                      key={col}
                      style={{
                        padding: '2px 6px',
                        color,
                        borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      {row[col] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Human-readable label for diff field keys
const formatFieldLabel = key => {
  const labels = {
    step_count: 'Step count',
    added_actions: 'Action added',
    removed_actions: 'Action removed',
    transformation_actions: 'Transformation actions'
  };
  return labels[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const DiffView = ({ diff }) => {
  if (!diff) return null;
  const keys = new Set([...Object.keys(diff.from || {}), ...Object.keys(diff.to || {})]);
  if (keys.size === 0) return null;
  return (
    <>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>
        {keys.size} field{keys.size !== 1 ? 's' : ''} changed
      </div>
      <DiffTable>
        <thead>
          <tr>
            <DiffCell as="th" style={{ color: '#374151', fontWeight: 700 }}>
              Field
            </DiffCell>
            <th style={{ padding: '4px 6px', fontSize: 11, color: '#dc2626', fontWeight: 700 }}>
              Before
            </th>
            <th style={{ padding: '4px 6px', fontSize: 11, color: '#16a34a', fontWeight: 700 }}>
              After
            </th>
          </tr>
        </thead>
        <tbody>
          {[...keys].map(key => {
            const fromVal = diff.from?.[key];
            const toVal = diff.to?.[key];
            return (
              <DiffRow key={key}>
                <DiffCell>{formatFieldLabel(key)}</DiffCell>
                <td style={{ padding: '4px 6px', fontSize: 12, verticalAlign: 'top' }}>
                  {isRowsData(fromVal) ? (
                    <RowsDisplay data={fromVal} type="before" />
                  ) : (
                    <DiffBefore>{formatDiffValue(fromVal)}</DiffBefore>
                  )}
                </td>
                <td style={{ padding: '4px 6px', fontSize: 12, verticalAlign: 'top' }}>
                  {isRowsData(toVal) ? (
                    <RowsDisplay data={toVal} type="after" />
                  ) : (
                    <DiffAfter>{formatDiffValue(toVal)}</DiffAfter>
                  )}
                </td>
              </DiffRow>
            );
          })}
        </tbody>
      </DiffTable>
    </>
  );
};

const buildDescription = (action, resourceType, resourceName) => {
  const type = resourceType
    ? resourceType.charAt(0).toUpperCase() + resourceType.slice(1).toLowerCase()
    : null;
  const name = resourceName ? `'${resourceName}'` : null;
  const subject = [type, name].filter(Boolean).join(' ');

  const templates = {
    CREATE: subject ? `${subject} was created.` : 'A new record was created.',
    UPDATE: subject ? `${subject} was updated.` : 'A record was updated.',
    DELETE: subject ? `${subject} was deleted.` : 'A record was deleted.',
    APPROVE: subject ? `${subject} was approved.` : 'The record was approved.',
    SUBMIT: subject ? `${subject} was submitted for review.` : 'The record was submitted.',
    EXPORT: subject ? `${subject} was exported.` : 'Data was exported.',
    RUN: subject ? `${subject} was run.` : 'A process was executed.',
    CANCEL: subject ? `${subject} was cancelled.` : 'An operation was cancelled.',
    INVITE: subject ? `${subject} was invited.` : 'An invitation was sent.',
    DEACTIVATE: subject ? `${subject} was deactivated.` : 'A record was deactivated.',
    LOGIN: 'User logged into the platform.',
    LOGOUT: 'User logged out of the platform.'
  };
  return templates[action] ?? (subject ? `${action} on ${subject}.` : null);
};

const formatTimestamp = iso => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });
};

const buildAiExplainQuestion = item => {
  const action = ACTION_LABELS[item.action] ?? item.action ?? 'UPDATE';
  const resourceType = RESOURCE_TYPE_LABELS[item.resource_type] ?? item.resource_type ?? 'resource';
  const resourceName = item.resource_name ?? null;
  return `Analyze this ${action} operation on ${resourceType}${resourceName ? ` "${resourceName}"` : ''} and explain exactly what changed in each field based on the full diff data.`;
};

const ActivityLogDetailPanel = ({
  entry,
  onClose,
  projectsById = {},
  periodsById = {},
  onOpenAiChat
}) => {
  // Fetch full detail (includes metadata JSONB)
  const { data: detail, isLoading } = useAuditLogDetail(entry?.id);
  const item = detail ?? entry;

  // Resize state
  const [width, setWidth] = useState(400);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);
  const handleRef = useRef(null);

  const onMouseDown = e => {
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = width;
    if (handleRef.current) handleRef.current.dataset.dragging = 'true';
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = ev => {
      if (!dragging.current) return;
      const delta = startX.current - ev.clientX; // drag left = grow
      setWidth(Math.min(800, Math.max(320, startW.current + delta)));
    };
    const onUp = () => {
      dragging.current = false;
      if (handleRef.current) handleRef.current.dataset.dragging = 'false';
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (!entry) return null;

  const ctx = item?.metadata?.context ?? {};
  const projectName = ctx.project_id ? (projectsById[ctx.project_id] ?? null) : null;
  const periodObj = ctx.period_id ? (periodsById[ctx.period_id] ?? null) : null;
  const periodLabel = periodObj
    ? `${periodObj.period} ${periodObj.year}`
    : (ctx.period_name ?? null);
  const deepLink = ctx.deep_link ?? null;

  return (
    <Drawer $width={width}>
      <DrawerResizeHandle ref={handleRef} onMouseDown={onMouseDown} title="Drag to resize" />
      <DrawerContent>
        <DrawerHeader>
          <div style={{ flex: 1 }}>
            <DrawerTitle>
              {ACTION_LABELS[entry.action] ?? entry.action}
              {entry.resource_type
                ? ` ${RESOURCE_TYPE_LABELS[entry.resource_type] ?? entry.resource_type}`
                : ''}
            </DrawerTitle>
            <DrawerSubtitle>{formatTimestamp(entry.timestamp)}</DrawerSubtitle>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            {onOpenAiChat && item && (
              <button
                onClick={() => onOpenAiChat(buildAiExplainQuestion(item), item.id)}
                title="Explain this entry with AI Analytics"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#228be6',
                  background: '#e7f5ff',
                  border: '1px solid #a5d8ff',
                  borderRadius: 6,
                  cursor: 'pointer',
                  lineHeight: '18px',
                  whiteSpace: 'nowrap'
                }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" />
                  <path d="M19 2 L19.7 4.3 L22 5 L19.7 5.7 L19 8 L18.3 5.7 L16 5 L18.3 4.3 Z" />
                  <path d="M5 16 L5.5 17.5 L7 18 L5.5 18.5 L5 20 L4.5 18.5 L3 18 L4.5 17.5 Z" />
                </svg>
                Explain with AI
              </button>
            )}
            <CloseBtn onClick={onClose}>×</CloseBtn>
          </div>
        </DrawerHeader>

        <DrawerBody>
          {isLoading && <Spinner />}

          {item && (
            <>
              <Field>
                <FieldLabel>Resource type</FieldLabel>
                <FieldValue style={{ textTransform: 'capitalize' }}>
                  {item.resource_type
                    ? (RESOURCE_TYPE_LABELS[item.resource_type] ?? item.resource_type)
                    : '—'}
                </FieldValue>
              </Field>

              <Field>
                <FieldLabel>Resource name</FieldLabel>
                <FieldValue>{item.resource_name ?? '—'}</FieldValue>
              </Field>

              {projectName && (
                <Field>
                  <FieldLabel>Project</FieldLabel>
                  <FieldValue>{projectName}</FieldValue>
                </Field>
              )}

              {periodLabel && (
                <Field>
                  <FieldLabel>Period</FieldLabel>
                  <FieldValue>{periodLabel}</FieldValue>
                </Field>
              )}

              {deepLink && (
                <Field>
                  <FieldLabel>Reference</FieldLabel>
                  <FieldValue>
                    <ReferenceLink href={deepLink} target="_blank" rel="noreferrer">
                      {deepLink}
                    </ReferenceLink>
                  </FieldValue>
                </Field>
              )}

              {item.resource_id && (
                <Field>
                  <FieldLabel>Resource ID</FieldLabel>
                  <FieldValue
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      color: '#6b7280',
                      wordBreak: 'break-all'
                    }}
                  >
                    {item.resource_id}
                  </FieldValue>
                </Field>
              )}

              <Field>
                <FieldLabel>Operation name</FieldLabel>
                <FieldValue>
                  <ActionBadge $action={item.action}>
                    {ACTION_LABELS[item.action] ?? item.action}
                  </ActionBadge>
                </FieldValue>
              </Field>

              {(item.status === 'FAILED' ||
                buildDescription(item.action, item.resource_type, item.resource_name)) && (
                <Field>
                  <FieldLabel>Description</FieldLabel>
                  {item.status === 'FAILED' ? (
                    <ErrorBox>
                      {item.error_message ||
                        'The operation failed. No further details are available.'}
                    </ErrorBox>
                  ) : (
                    <FieldValue style={{ color: '#6b7280', fontStyle: 'italic' }}>
                      {buildDescription(item.action, item.resource_type, item.resource_name)}
                    </FieldValue>
                  )}
                </Field>
              )}

              {item.metadata?.diff && (
                <Field>
                  <FieldLabel>Changes</FieldLabel>
                  <FieldValue>
                    <DiffView diff={item.metadata.diff} />
                  </FieldValue>
                </Field>
              )}

              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>
                  <StatusChip $status={item.status}>{item.status}</StatusChip>
                  {item.status_code && (
                    <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 8 }}>
                      HTTP {item.status_code}
                    </span>
                  )}
                </FieldValue>
              </Field>

              <Field>
                <FieldLabel>Time stamp</FieldLabel>
                <FieldValue>{formatTimestamp(item.timestamp)}</FieldValue>
              </Field>

              <Field>
                <FieldLabel>Event initiated by</FieldLabel>
                <FieldValue>
                  {item.user_name ?? item.user_id ?? 'System'}
                  {item.user_email && (
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.user_email}</div>
                  )}
                </FieldValue>
              </Field>

              {item.ip_address && (
                <Field>
                  <FieldLabel>IP address</FieldLabel>
                  <FieldValue>{item.ip_address}</FieldValue>
                </Field>
              )}

              {item.duration_ms != null && (
                <Field>
                  <FieldLabel>Duration</FieldLabel>
                  <FieldValue>{item.duration_ms}ms</FieldValue>
                </Field>
              )}
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ActivityLogDetailPanel;
